<script>
import * as routeHelpers from "../../../services/route-helpers";
import AnimatedChevron from "../Icons/AnimatedChevron.vue";

export default {
  props: {
    overviewPagesData: {
      type: [Array, null],
      required: false,
    },
    portalMenuIsOpen: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      currentPath: window.location.pathname,
    };
  },
  computed: {
    ctaData() {
      return this.getCtaData();
    },
    startPageHref() {
      return this.getStartPageUrl();
    },
    aboutUsHref() {
      return this.buildUrlWithSource("ueber-anni");
    },
  },
  emits: ["expandMenu", "collapseMenu", "toggleMenu"],
  methods: {
    async onTriggerMouseOver(e) {
      // check if the relatedTarget is a child of the trigger
      if (e.relatedTarget?.closest(".expanded-content-trigger")) {
        return;
      }
      // check if the relatedTarget is the header
      if (e.relatedTarget?.classList.contains("desktop-header-container")) {
        return;
      }
      // check if the relatedTarget is the parent of the trigger
      if (e.relatedTarget?.classList.contains("desktop-header-container")) {
        return;
      }
      this.$emit("collapseMenu");
    },
    ...routeHelpers,
  },
  components: { AnimatedChevron },
};
</script>

<template>
  <ul class="desktop-menu desktop-header-container">
    <li
      @mouseover="$emit('collapseMenu')"
      class="nav-item"
      :class="{
        active:
          currentPageIs('/') ||
          currentPageIs('/patienten') ||
          currentPageIs('/rezept-express'),
        'xl-only': isB2BRoute(),
      }"
    >
      <a :href="startPageHref"> Startseite </a>
    </li>

    <li
      v-if="isB2BRoute()"
      class="nav-item"
      :class="{ active: currentPageIs('/module/mdr') }"
    >
      <a href="/module/mdr"> EasyCompliance </a>
    </li>

    <li
      v-if="isB2BRoute()"
      class="nav-item"
      :class="{ active: currentPageIs('/module/status') }"
    >
      <a href="/module/status"> StatusSync </a>
    </li>

    <li
      v-if="isB2BRoute()"
      class="nav-item"
      :class="{ active: currentPageIs('/module/chatbot') }"
    >
      <a href="/module/chatbot"> Online-Rezeption </a>
    </li>

    <li
      v-if="isPatientRoute()"
      class="nav-item expanded-content-trigger"
      :class="{ active: isInfoPage(overviewPagesData) }"
      @mouseover="$emit('expandMenu')"
      @mouseout="onTriggerMouseOver"
      id="expanded-content-trigger"
    >
      <button
        @click.prevent="$emit('toggleMenu')"
        :aria-expanded="portalMenuIsOpen"
        aria-controls="header-expanded-content"
      >
        <span>Informationen</span>
        <i>
          <AnimatedChevron :is-expanded="portalMenuIsOpen" />
        </i>
      </button>
    </li>

    <li
      v-if="isRxpRoute() || isPatientRoute()"
      class="nav-item"
      @mouseover="$emit('collapseMenu')"
    >
      <a href="/"> Für Sanitätshäuser </a>
    </li>

    <li
      @mouseover="$emit('collapseMenu')"
      class="nav-item"
      :class="{ active: currentPageIs('/ueber-anni') }"
    >
      <a :href="aboutUsHref"> Über uns </a>
    </li>

    <a
      v-if="ctaData"
      :href="ctaData.url"
      :target="ctaData.target"
      class="anni-cta-button"
      @mouseover="$emit('collapseMenu')"
      role="listitem"
    >
      Einloggen
    </a>

    <span v-else class="anni-cta-button button-spacer" role="listitem" />
  </ul>
</template>

<style scoped lang="scss">
.desktop-header-container {
  display: none;
  align-items: center;
  gap: 40px;
  margin: 0;
  padding: 0;
  height: 100%;
  width: 100%;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;

  .nav-item {
    height: 100%;
    display: block;

    &.xl-only,
    &.xxl-only {
      display: none;
    }

    a {
      text-decoration: none;
      font-weight: 400;
      color: #000;

      &:hover {
        color: #35887e;
      }
    }

    button {
      border: none;
      cursor: pointer;
      background-color: transparent;
      border-radius: 4px;
      display: flex;
      align-items: center;
      font-weight: 400;
      color: #000;

      i {
        display: flex;
        align-items: center;
      }

      &:hover {
        color: #35887e;
      }
    }

    &.active {
      a,
      button {
        color: #35887e;
        font-weight: 600;
      }
    }
  }

  .anni-cta-button {
    margin-left: auto;
    background-color: #35887e;
    font-weight: 600;
    text-align: center;
    color: #fff;
    border: none;
    text-decoration: none;
    border-radius: 6px;
    padding: 10px 18px;
    line-height: 134%;
    border: none;
    cursor: pointer;
    display: block;
    width: 100%;
    max-width: 120px;
    transition: background-color 0.1s ease-in-out, max-width 0.2s ease-in-out;

    &.button-spacer {
      visibility: hidden;
      width: 0;
      height: 41.44px;
      padding: 0;
    }

    @media (hover: hover) {
      &:hover:not(:disabled) {
        background-color: #225952;
      }
    }

    &:focus-visible {
      outline: 3px solid;
      outline-offset: 3px;
      outline-color: #35887e;
    }

    &:disabled {
      color: #989898;
      border-color: #989898;
      cursor: not-allowed;
    }
  }
}

@media (min-width: 961px) {
  .desktop-header-container {
    display: flex;
  }
}

@media (min-width: 1080px) {
  .desktop-header-container .nav-item.xl-only {
    display: block;
  }
}

@media (min-width: 1200px) {
  .desktop-header-container {
    font-size: 18px;

    .anni-cta-button {
      max-width: 160px;

      &.button-spacer {
        height: 44.13px;
      }
    }
  }
}

@media (min-width: 1280px) {
  .desktop-header-container .nav-item.xxl-only {
    display: block;
  }

  .desktop-header-container {
    .anni-cta-button {
      max-width: 200px;
    }
  }
}

@media (min-width: 1440px) {
  .desktop-header-container {
    // font-size: 20px;

    .anni-cta-button {
      max-width: 240px;
    }
  }
}
</style>
